import { z } from 'zod'
import {
  calculateMonthlyPayment,
  calculateCapRate,
  calculateCashOnCash,
  calculateGRM,
  calculateDSCR,
  calculateMonthlyCashFlow,
} from './formulas'

// =============================================================================
// Deal Schema
// =============================================================================

export const propertyTypeSchema = z.enum(['single-family', 'duplex', 'triplex', 'fourplex'])
export type PropertyType = z.infer<typeof propertyTypeSchema>

export const dealStatusSchema = z.enum(['analyzing', 'passed', 'failed', 'archived'])
export type DealStatus = z.infer<typeof dealStatusSchema>

export const dealInputSchema = z.object({
  // Property Info
  address: z.string().min(1, 'Address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().length(2, 'State must be 2 characters'),
  zip: z.string().optional(),
  propertyType: propertyTypeSchema,
  units: z.number().min(1).max(4),
  yearBuilt: z.number().optional(),
  sqft: z.number().optional(),

  // Purchase
  purchasePrice: z.number().min(1, 'Purchase price is required'),
  downPaymentPercent: z.number().min(0).max(100),
  closingCosts: z.number().min(0),
  rehabCosts: z.number().min(0),

  // Loan
  interestRate: z.number().min(0).max(30),
  loanTermYears: z.number().min(1).max(40),

  // Income
  monthlyRent: z.number().min(0),
  otherMonthlyIncome: z.number().min(0),
  vacancyRate: z.number().min(0).max(100),

  // Expenses (monthly)
  propertyTax: z.number().min(0),
  insurance: z.number().min(0),
  hoa: z.number().min(0),
  maintenance: z.number().min(0),
  propertyManagement: z.number().min(0),
  utilities: z.number().min(0),
  otherExpenses: z.number().min(0),

  // Metadata
  name: z.string().optional(),
  notes: z.string().optional(),
})

export type DealInput = z.infer<typeof dealInputSchema>

export interface Deal extends DealInput {
  id: string
  userId: string
  status: DealStatus
  createdAt: string
  updatedAt: string
}

// =============================================================================
// Calculated Metrics
// =============================================================================

export interface CalculatedMetrics {
  // Loan calculations
  loanAmount: number
  downPayment: number
  monthlyPayment: number
  totalCashInvested: number

  // Income/Expense
  grossMonthlyIncome: number
  effectiveGrossIncome: number
  totalMonthlyExpenses: number
  noi: number
  annualDebtService: number

  // Key metrics
  capRate: number
  cashOnCash: number
  grm: number
  dscr: number
  monthlyCashFlow: number
  annualCashFlow: number
}

export function calculateDealMetrics(deal: DealInput): CalculatedMetrics {
  const loanAmount = deal.purchasePrice * (1 - deal.downPaymentPercent / 100)
  const downPayment = deal.purchasePrice * (deal.downPaymentPercent / 100)
  const monthlyPayment = calculateMonthlyPayment(loanAmount, deal.interestRate, deal.loanTermYears)
  const totalCashInvested = downPayment + deal.closingCosts + deal.rehabCosts

  const grossMonthlyIncome = deal.monthlyRent + deal.otherMonthlyIncome
  const effectiveGrossIncome = grossMonthlyIncome * (1 - deal.vacancyRate / 100) * 12

  const totalMonthlyExpenses =
    deal.propertyTax +
    deal.insurance +
    deal.hoa +
    deal.maintenance +
    deal.propertyManagement +
    deal.utilities +
    deal.otherExpenses

  const noi = effectiveGrossIncome - totalMonthlyExpenses * 12
  const annualDebtService = monthlyPayment * 12

  const capRate = calculateCapRate(noi, deal.purchasePrice)
  const annualCashFlow = noi - annualDebtService
  const cashOnCash = calculateCashOnCash(annualCashFlow, totalCashInvested)
  const grm = calculateGRM(deal.purchasePrice, grossMonthlyIncome * 12)

  // DSCR uses effective monthly income vs total monthly debt service
  const effectiveMonthlyIncome = grossMonthlyIncome * (1 - deal.vacancyRate / 100)
  const dscr = calculateDSCR(effectiveMonthlyIncome, monthlyPayment)

  const monthlyCashFlow = calculateMonthlyCashFlow(
    grossMonthlyIncome,
    deal.vacancyRate,
    totalMonthlyExpenses,
    monthlyPayment
  )

  return {
    loanAmount,
    downPayment,
    monthlyPayment,
    totalCashInvested,
    grossMonthlyIncome,
    effectiveGrossIncome,
    totalMonthlyExpenses,
    noi,
    annualDebtService,
    capRate,
    cashOnCash,
    grm,
    dscr,
    monthlyCashFlow,
    annualCashFlow,
  }
}

// =============================================================================
// User Criteria Schema
// =============================================================================

export const userCriteriaSchema = z.object({
  minCapRate: z.number().optional(),
  minCashOnCash: z.number().optional(),
  maxGRM: z.number().optional(),
  minDSCR: z.number().optional(),
  minMonthlyCashFlow: z.number().optional(),
  maxPurchasePrice: z.number().optional(),
  maxDownPayment: z.number().optional(),
})

export type UserCriteria = z.infer<typeof userCriteriaSchema>

// =============================================================================
// Go/No-Go Evaluation
// =============================================================================

export interface CriterionCheck {
  criterion: string
  threshold: number
  actual: number
  passed: boolean
  comparisonType: 'min' | 'max'
}

export interface GoNoGoResult {
  verdict: 'pass' | 'fail' | 'warning'
  checks: CriterionCheck[]
  passedCount: number
  totalChecks: number
}

export function evaluateGoNoGo(metrics: CalculatedMetrics, criteria: UserCriteria): GoNoGoResult {
  const checks: CriterionCheck[] = []

  if (criteria.minCapRate !== undefined) {
    checks.push({
      criterion: 'Cap Rate',
      threshold: criteria.minCapRate,
      actual: metrics.capRate,
      passed: metrics.capRate >= criteria.minCapRate,
      comparisonType: 'min',
    })
  }

  if (criteria.minCashOnCash !== undefined) {
    checks.push({
      criterion: 'Cash-on-Cash',
      threshold: criteria.minCashOnCash,
      actual: metrics.cashOnCash,
      passed: metrics.cashOnCash >= criteria.minCashOnCash,
      comparisonType: 'min',
    })
  }

  if (criteria.maxGRM !== undefined) {
    checks.push({
      criterion: 'GRM',
      threshold: criteria.maxGRM,
      actual: metrics.grm,
      passed: metrics.grm <= criteria.maxGRM,
      comparisonType: 'max',
    })
  }

  if (criteria.minDSCR !== undefined) {
    checks.push({
      criterion: 'DSCR',
      threshold: criteria.minDSCR,
      actual: metrics.dscr,
      passed: metrics.dscr >= criteria.minDSCR,
      comparisonType: 'min',
    })
  }

  if (criteria.minMonthlyCashFlow !== undefined) {
    checks.push({
      criterion: 'Monthly Cash Flow',
      threshold: criteria.minMonthlyCashFlow,
      actual: metrics.monthlyCashFlow,
      passed: metrics.monthlyCashFlow >= criteria.minMonthlyCashFlow,
      comparisonType: 'min',
    })
  }

  const passedCount = checks.filter((c) => c.passed).length
  const totalChecks = checks.length

  let verdict: 'pass' | 'fail' | 'warning'
  if (totalChecks === 0) {
    verdict = 'pass' // No criteria set
  } else if (passedCount === totalChecks) {
    verdict = 'pass'
  } else if (passedCount >= totalChecks * 0.75) {
    verdict = 'warning'
  } else {
    verdict = 'fail'
  }

  return { verdict, checks, passedCount, totalChecks }
}

// =============================================================================
// Criteria Presets
// =============================================================================

export const criteriaPresets = {
  conservative: {
    minCapRate: 10,
    minCashOnCash: 15,
    maxGRM: 8,
    minDSCR: 1.5,
    minMonthlyCashFlow: 300,
  },
  moderate: {
    minCapRate: 8,
    minCashOnCash: 10,
    maxGRM: 10,
    minDSCR: 1.25,
    minMonthlyCashFlow: 200,
  },
  aggressive: {
    minCapRate: 6,
    minCashOnCash: 8,
    maxGRM: 12,
    minDSCR: 1.1,
    minMonthlyCashFlow: 100,
  },
  // Cleveland Westside triplex/fourplex criteria (Fairmeadow Capital 2026)
  // Target: Detroit Shoreway, Birdtown, West Lakewood
  // Based on $1,200/unit rent, 25% down, 7.5% rate
  cleveland: {
    minCapRate: 8,
    minCashOnCash: 10,
    maxGRM: 8,
    minDSCR: 1.25,
    minMonthlyCashFlow: 600, // ~$200/unit for triplex
  },
} as const
